import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useLocation } from 'react-router-dom';
import {
  Upload, Play, Palette, Download, Sliders, Thermometer, Info,
  Image as ImageIcon,
} from 'lucide-react';
import { API } from '@/App';

const KINDS = [
  { id: 'leaf_greenness', label: 'Leaf greenness (NDGI)', blurb: 'From Leaf/Canopy sub-tab' },
  { id: 'disease_lesion', label: 'Disease lesion intensity', blurb: 'From Disease sub-tab' },
  { id: 'root_depth', label: 'Root depth density', blurb: 'From Root Analysis sub-tab' },
  { id: 'nir_moisture', label: 'NIR moisture', blurb: 'From NIR Prediction — bulk fill over canopy' },
];

const COLORMAPS = ['viridis', 'plasma', 'RdYlGn', 'jet', 'magma', 'cividis', 'turbo'];

export default function Heatmap() {
  const location = useLocation();
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [kind, setKind] = useState('leaf_greenness');
  const [colormap, setColormap] = useState('RdYlGn');
  const [alpha, setAlpha] = useState(0.5);
  const [dpi, setDpi] = useState(300);
  const [scalarValue, setScalarValue] = useState(12.0);
  const [pixelsPerCm, setPixelsPerCm] = useState('');
  const [titleText, setTitleText] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  // ── Pre-load from source sub-tab ("View as Heatmap" button) ────────────
  useEffect(() => {
    const preset = location?.state?.heatmapPreset;
    if (!preset) return;
    if (preset.kind) setKind(preset.kind);
    if (preset.colormap) setColormap(preset.colormap);
    if (preset.scalarValue != null) setScalarValue(preset.scalarValue);
    if (preset.pixelsPerCm) setPixelsPerCm(String(preset.pixelsPerCm));
    if (preset.title) setTitleText(preset.title);
    if (preset.imageBase64) {
      fetch(`data:image/png;base64,${preset.imageBase64}`).then(r => r.blob()).then(blob => {
        const f = new File([blob], preset.filename || 'preset.png', { type: 'image/png' });
        setFile(f); setImagePreview(URL.createObjectURL(blob));
      });
    }
  }, [location]);

  const defaultCmapForKind = useMemo(() => ({
    leaf_greenness: 'RdYlGn', disease_lesion: 'plasma',
    root_depth: 'viridis', nir_moisture: 'turbo',
  }), []);

  const onKindChange = (k) => {
    setKind(k); setColormap(defaultCmapForKind[k] || 'viridis');
  };

  const onPickFile = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setFile(f); setImagePreview(URL.createObjectURL(f)); setResult(null);
  };

  const loadSampleFor = async (src) => {
    setBusy(true);
    try {
      let url;
      if (src === 'leaf') url = `${API}/phenoscan/leaf/sample`;
      else if (src === 'disease') url = `${API}/phenoscan/disease/sample`;
      else if (src === 'root') url = `${API}/phenoscan/root/sample/3`;
      const r = await axios.get(url, { responseType: 'blob' });
      const f = new File([r.data], `${src}_sample.png`, { type: 'image/png' });
      setFile(f); setImagePreview(URL.createObjectURL(r.data)); setResult(null);
      // Auto-select kind
      if (src === 'leaf') { setKind('leaf_greenness'); setColormap('RdYlGn'); }
      if (src === 'disease') { setKind('disease_lesion'); setColormap('plasma'); }
      if (src === 'root') { setKind('root_depth'); setColormap('viridis'); }
      toast.success(`Loaded ${src} sample`);
    } catch { toast.error('Failed to load sample'); }
    setBusy(false);
  };

  const render = async () => {
    if (!file) return toast.error('Upload or load a sample image first');
    setBusy(true); setResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file); fd.append('kind', kind);
      fd.append('colormap', colormap); fd.append('alpha', String(alpha));
      fd.append('dpi', String(dpi));
      if (titleText) fd.append('title', titleText);
      if (kind === 'nir_moisture') fd.append('scalar_value', String(scalarValue));
      if (pixelsPerCm) fd.append('pixels_per_cm', String(pixelsPerCm));
      const r = await axios.post(`${API}/phenoscan/heatmap/render`, fd);
      setResult(r.data.results);
      toast.success(`Heatmap rendered (${r.data.results.png_size_bytes.toLocaleString()} bytes)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Render failed');
    } finally { setBusy(false); }
  };

  const downloadPNG = () => {
    if (!result) return;
    const bin = atob(result.png_base64);
    const arr = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
    const blob = new Blob([arr], { type: 'image/png' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `phenoscan_heatmap_${kind}_${colormap}_${dpi}dpi.png`; a.click();
    URL.revokeObjectURL(url);
    toast.success(`${dpi} DPI PNG downloaded`);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[400px_1fr] gap-6" data-testid="heatmap-pane">
      {/* LEFT controls */}
      <div className="rounded-2xl p-5 bg-white" style={{ border: '1px solid #E5E5E0' }}>
        <h3 className="font-display text-lg mb-3" style={{ color: '#1B4332' }}>1. Image</h3>
        <div onClick={() => fileRef.current?.click()} data-testid="heatmap-upload-zone"
          className="rounded-xl p-4 mb-3 text-center cursor-pointer"
          style={{ border: '1px dashed #BCC5B5', background: '#FAFBF9' }}>
          {imagePreview ?
            <img src={imagePreview} alt="Upload"
              style={{ maxHeight: 160, margin: '0 auto', borderRadius: 8 }} /> :
            <div className="py-6 text-sm" style={{ color: '#6F695F' }}>
              <Upload size={22} className="mx-auto mb-2" /> Click or drag an image
            </div>
          }
          <input ref={fileRef} type="file" accept="image/*" className="hidden"
            onChange={onPickFile} data-testid="heatmap-upload-input" />
        </div>
        <div className="text-xs mb-1" style={{ color: '#4F4B44' }}>Load a source sample:</div>
        <div className="grid grid-cols-3 gap-1 mb-4">
          <button onClick={() => loadSampleFor('leaf')} disabled={busy}
            className="rounded-lg py-1.5 text-xs" style={{ background: '#ECFDF5', color: '#065F46', border: '1px solid #A7F3D0' }}
            data-testid="heatmap-load-leaf">Leaf</button>
          <button onClick={() => loadSampleFor('disease')} disabled={busy}
            className="rounded-lg py-1.5 text-xs" style={{ background: '#ECFDF5', color: '#065F46', border: '1px solid #A7F3D0' }}
            data-testid="heatmap-load-disease">Disease</button>
          <button onClick={() => loadSampleFor('root')} disabled={busy}
            className="rounded-lg py-1.5 text-xs" style={{ background: '#ECFDF5', color: '#065F46', border: '1px solid #A7F3D0' }}
            data-testid="heatmap-load-root">Root</button>
        </div>

        <h3 className="font-display text-lg mb-3" style={{ color: '#1B4332' }}>2. Heatmap kind</h3>
        <div className="space-y-1 mb-4">
          {KINDS.map(k => (
            <label key={k.id}
              data-testid={`heatmap-kind-${k.id}`}
              className="flex items-start gap-2 rounded-md px-2 py-1.5 cursor-pointer"
              style={{ background: kind === k.id ? '#F3F6F3' : 'transparent',
                border: `1px solid ${kind === k.id ? '#2D6A4F' : '#E5E5E0'}` }}>
              <input type="radio" name="heatmap-kind" value={k.id}
                checked={kind === k.id} onChange={() => onKindChange(k.id)} />
              <div>
                <div className="text-sm" style={{ color: '#1B4332', fontWeight: 600 }}>{k.label}</div>
                <div className="text-xs" style={{ color: '#6F695F' }}>{k.blurb}</div>
              </div>
            </label>
          ))}
        </div>

        {kind === 'nir_moisture' && (
          <label className="block mb-3 text-sm" data-testid="heatmap-scalar-wrap">
            <div className="flex justify-between mb-1" style={{ color: '#4F4B44' }}>
              <span>Predicted moisture (%)</span>
              <span style={{ color: '#1B4332', fontWeight: 600 }}>{scalarValue.toFixed(1)}</span>
            </div>
            <input type="number" min={0} max={100} step={0.1} value={scalarValue}
              onChange={e => setScalarValue(parseFloat(e.target.value) || 0)}
              className="w-full rounded px-3 py-1.5"
              style={{ border: '1px solid #E5E5E0' }}
              data-testid="heatmap-scalar" />
          </label>
        )}

        <h3 className="font-display text-lg mb-3 mt-5" style={{ color: '#1B4332' }}>3. Render</h3>

        <label className="block mb-3 text-sm">
          <div className="mb-1 flex items-center gap-1" style={{ color: '#4F4B44' }}>
            <Palette size={12} /> Colormap
          </div>
          <select value={colormap} onChange={e => setColormap(e.target.value)}
            data-testid="heatmap-colormap"
            className="w-full rounded px-3 py-1.5 text-sm bg-white"
            style={{ border: '1px solid #E5E5E0' }}>
            {COLORMAPS.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>

        <label className="block mb-3 text-sm">
          <div className="flex justify-between mb-1" style={{ color: '#4F4B44' }}>
            <span>Overlay alpha</span>
            <span style={{ color: '#1B4332', fontWeight: 600 }}>{alpha.toFixed(2)}</span>
          </div>
          <input type="range" min={0} max={1} step={0.05} value={alpha}
            onChange={e => setAlpha(parseFloat(e.target.value))}
            data-testid="heatmap-alpha" className="w-full" />
        </label>

        <label className="block mb-3 text-sm">
          <div className="mb-1" style={{ color: '#4F4B44' }}>
            DPI (300 = publication)
          </div>
          <select value={dpi} onChange={e => setDpi(parseInt(e.target.value, 10))}
            data-testid="heatmap-dpi"
            className="w-full rounded px-3 py-1.5 text-sm bg-white"
            style={{ border: '1px solid #E5E5E0' }}>
            <option value={72}>72 (screen)</option>
            <option value={150}>150 (preview)</option>
            <option value={300}>300 (publication)</option>
            <option value={600}>600 (print)</option>
          </select>
        </label>

        <label className="block mb-3 text-sm">
          <div className="mb-1" style={{ color: '#4F4B44' }}>Title (optional)</div>
          <input value={titleText} onChange={e => setTitleText(e.target.value)}
            placeholder="e.g. Drought trial · Genotype B73 · Day 14"
            className="w-full rounded px-3 py-1.5"
            style={{ border: '1px solid #E5E5E0' }}
            data-testid="heatmap-title" />
        </label>

        <label className="block mb-4 text-sm">
          <div className="mb-1" style={{ color: '#4F4B44' }}>Scale bar — pixels / cm (optional)</div>
          <input type="number" step="0.1" min="0" value={pixelsPerCm}
            onChange={e => setPixelsPerCm(e.target.value)}
            placeholder="leave empty if uncalibrated"
            className="w-full rounded px-3 py-1.5"
            style={{ border: '1px solid #E5E5E0' }}
            data-testid="heatmap-pxcm" />
        </label>

        <button onClick={render} disabled={busy || !file} data-testid="heatmap-render-btn"
          className="w-full rounded-lg py-2.5 text-sm font-semibold disabled:opacity-50"
          style={{ background: '#1B4332', color: '#fff' }}>
          <Play size={14} className="inline mr-2" /> {busy ? 'Rendering…' : 'Render Heatmap'}
        </button>
      </div>

      {/* RIGHT preview */}
      <div className="rounded-2xl p-5 bg-white" style={{ border: '1px solid #E5E5E0' }}>
        {!result ? (
          <div className="text-center py-16">
            <Thermometer size={32} className="mx-auto mb-3" style={{ color: '#BCC5B5' }} />
            <div className="font-display text-lg" style={{ color: '#6F695F' }}>No heatmap rendered yet</div>
            <div className="text-sm mt-1" style={{ color: '#8A8580' }}>
              Load a source sample or upload an image, pick the heatmap kind, and click Render.
            </div>
          </div>
        ) : (
          <div data-testid="heatmap-results">
            <div className="flex items-center gap-2 mb-3">
              <span className="px-2.5 py-1 rounded-full text-xs"
                data-testid="heatmap-meta-badge"
                style={{ background: '#F3F6F3', color: '#1B4332' }}>
                {result.kind} · {result.colormap} · α={result.alpha} · {result.dpi} DPI ·
                {' '}{result.width_px}×{result.height_px}px · {(result.png_size_bytes / 1024).toFixed(0)} KB
              </span>
              <button onClick={downloadPNG} data-testid="heatmap-download"
                className="ml-auto px-3 py-1.5 rounded-md text-xs inline-flex items-center gap-1"
                style={{ background: '#ECFDF5', border: '1px solid #A7F3D0', color: '#065F46' }}>
                <Download size={11} /> Download {result.dpi} DPI PNG
              </button>
            </div>
            <div className="rounded-xl overflow-hidden" style={{ border: '1px solid #E5E5E0' }}>
              <img data-testid="heatmap-overlay-img" src={`data:image/png;base64,${result.png_base64}`}
                alt="Heatmap overlay" style={{ maxWidth: '100%', display: 'block' }} />
            </div>

            {/* Determinism hash + info */}
            <div className="rounded-xl p-3 mt-4"
              data-testid="heatmap-method"
              style={{ background: '#FAFBF9', border: '1px solid #E5E5E0' }}>
              <div className="text-xs uppercase tracking-wide mb-2" style={{ color: '#4F4B44' }}>
                <Info size={11} className="inline mr-1" /> Method &amp; reproducibility
              </div>
              <div className="text-xs" style={{ color: '#4F4B44' }}>
                <div><span style={{ color: '#6F695F' }}>Title:</span> <b style={{ color: '#1B4332' }}>{result.title}</b></div>
                <div><span style={{ color: '#6F695F' }}>Unit:</span> {result.unit}</div>
                <div><span style={{ color: '#6F695F' }}>Value range shown:</span>{' '}
                  [{result.value_range[0]}, {result.value_range[1]}]</div>
                <div className="mt-1" style={{ color: '#6F695F' }}>
                  Byte-hash (sha256 prefix): <code style={{ color: '#1B4332' }}>{result.png_bytes_hash.substring(0, 32)}</code>
                </div>
                <div className="mt-2 text-[11px]" style={{ color: '#4F4B44' }}>
                  Heatmap does <u>not</u> compute new traits. Use the source sub-tab (Leaf / Disease / Root / NIR)
                  to hand off trait matrices to the BKAgriD stats modules.
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
